import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../api";
import { MarketTag } from "../../components/MarketTag";
import { SortableTable } from "../../components/SortableTable";
import { pct, timeAgo, tone } from "../../format";
import { Alert, Card, Stat } from "../../shell/ui";
import { BotState } from "../social/bots";
import { AlertList, HealthDot, Meter, bytes, since, useOpsAlerts } from "./opsShared";
import type { Health, OpsAlert } from "./opsShared";
import { directionColumn } from "../../components/direction";

/** Admin → Bots: the fleet's health and resources, each bot's process and log, and the feeds. */

interface Resources {
  rssBytes: number;
  heapUsedBytes: number;
  cpuPct: number | null;
  sampledAt: number;
}

interface Process {
  id: string;
  running: boolean;
  process: null | { pid: number | null; startedAt: number; crashes: number; resources: Resources | null };
}

interface Feed {
  stream: string;
  subscribers: number;
  state: string;
  stateSince: number;
  lastCandleAt: number | null;
  reconnects: number;
  timeframe: string;
}

interface FleetBot {
  id: string;
  name: string;
  symbol: string;
  timeframe: string;
  market_type: string;
  leverage: number;
  direction?: "long" | "short";
  status: string;
  desired_state: string;
  visibility: string;
  return_pct: number | null;
  max_drawdown_pct: number | null;
  equity: number | null;
  trade_count: number;
  last_bar_at: number | null;
  gap_count: number;
  started_at: string | null;
  username: string;
  is_simulated: boolean;
  errors24h: number;
  warnings24h: number;
  gaps24h: number;
}

interface Beat {
  memBytes: number | null;
  memLimitBytes: number | null;
  cpuPct: number | null;
  cpuLimit: number | null;
  load1: number;
  hostCpus: number;
  startedAt: number;
  extra?: { rssBytes?: number; cpuPct?: number; running?: number; restoring?: number };
}

interface FleetResponse {
  bots: FleetBot[];
  queue: Record<string, number>;
  runner: null | { feeds: Feed[]; processes: Process[]; restoring: number };
  runnerBeat: Beat | null;
  maxBots: number;
}

interface LogLine {
  id: number;
  bot_id: string | null;
  bot_name: string | null;
  level: string;
  source: string;
  message: string;
  created_at: string;
}

const VIEWS = ["Fleet", "Logs", "Feeds"] as const;
type View = (typeof VIEWS)[number];

function useFleet() {
  return useQuery({
    queryKey: ["admin", "fleet"],
    queryFn: () => api<FleetResponse>("/admin/fleet"),
    refetchInterval: 15_000,
  });
}

function healthOf(b: FleetBot, issues: OpsAlert[] | undefined): Health {
  if (b.desired_state !== "running") return "off";
  if (issues?.some((a) => a.severity === "error")) return "error";
  if (issues?.length) return "warn";
  return "ok";
}

const cpu = (n: number | null | undefined) => (n == null ? "—" : `${n.toFixed(n < 10 ? 1 : 0)}%`);

export function BotsTab() {
  const [view, setView] = useState<View>("Fleet");
  const [logBot, setLogBot] = useState("");
  return (
    <div className="stack">
      <div className="segmented" role="group" aria-label="Bots view">
        {VIEWS.map((v) => (
          <button
            key={v}
            type="button"
            className={view === v ? "is-active" : undefined}
            aria-pressed={view === v}
            onClick={() => setView(v)}
          >
            {v}
          </button>
        ))}
      </div>
      {view === "Fleet" && (
        <FleetView
          onLogs={(id) => {
            setLogBot(id);
            setView("Logs");
          }}
        />
      )}
      {view === "Logs" && <LogsView bot={logBot} onBot={setLogBot} />}
      {view === "Feeds" && <FeedsView />}
    </div>
  );
}

function FleetView({ onLogs }: { onLogs: (botId: string) => void }) {
  const q = useFleet();
  const alerts = useOpsAlerts();
  const [filter, setFilter] = useState<"all" | "problems" | "running" | "stopped">("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  const botAlerts = (alerts.data?.items ?? []).filter((a) => a.area === "bots");
  const issues = useMemo(() => {
    const m = new Map<string, OpsAlert[]>();
    for (const a of botAlerts) if (a.botId) m.set(a.botId, [...(m.get(a.botId) ?? []), a]);
    return m;
  }, [botAlerts]);
  const proc = new Map((q.data?.runner?.processes ?? []).map((p) => [p.id, p]));
  const bots = q.data?.bots ?? [];
  const running = bots.filter((b) => b.desired_state === "running");
  const healthCount = (h: Health) => running.filter((b) => healthOf(b, issues.get(b.id)) === h).length;
  const needle = search.trim().toLowerCase();
  const rows = bots.filter((b) => {
    const h = healthOf(b, issues.get(b.id));
    if (filter === "problems" && (h === "ok" || h === "off") && !b.errors24h) return false;
    if (filter === "running" && b.desired_state !== "running") return false;
    if (filter === "stopped" && b.desired_state === "running") return false;
    if (!needle) return true;
    return `${b.name} ${b.username} ${b.symbol} ${b.timeframe}`.toLowerCase().includes(needle);
  });
  const beat = q.data?.runnerBeat;
  const selected = open ? bots.find((b) => b.id === open) : undefined;

  useEffect(() => {
    if (open) detailRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [open]);

  return (
    <div className="stack">
      <Card title="Needs attention">
        <AlertList items={botAlerts} loading={alerts.isPending} />
      </Card>
      <Card title="Fleet">
        <div className="stat-grid stat-grid--pairs">
          <Stat
            label="Running"
            value={q.data ? `${running.length} / ${q.data.maxBots}` : "—"}
            hint="Bots meant to run, of MAX_BOTS_TOTAL"
          />
          <Stat label="Healthy" value={q.data ? healthCount("ok") : "—"} />
          <Stat label="Warnings" value={q.data ? healthCount("warn") : "—"} />
          <Stat label="Problems" value={q.data ? healthCount("error") : "—"} />
          <Stat label="Stopped" value={q.data ? bots.length - running.length : "—"} />
          <Stat
            label="Jobs waiting / failed"
            value={q.data ? `${q.data.queue.waiting ?? 0} / ${q.data.queue.failed ?? 0}` : "—"}
            hint="The compute queue: backtests, sweeps, verifies"
          />
        </div>
        {q.data && !q.data.runner && (
          <Alert tone="error">
            The bot runner (hub-bots) did not answer. Processes and feeds are unknown.
          </Alert>
        )}
      </Card>
      <Card title="Resources (hub-bots)">
        {beat ? (
          <div className="ops-resources">
            <div>
              <div className="field__label">Container memory</div>
              <Meter
                value={beat.memBytes}
                limit={beat.memLimitBytes}
                format={bytes}
                label="hub-bots memory"
              />
            </div>
            <div>
              <div className="field__label">Container CPU</div>
              <Meter
                value={beat.cpuPct}
                limit={beat.cpuLimit ? beat.cpuLimit * 100 : null}
                format={(n) => `${Math.round(n)}%`}
                label="hub-bots CPU"
              />
            </div>
            <div>
              <div className="field__label">Bot processes</div>
              <span className="mono">
                {bytes(beat.extra?.rssBytes ?? null)} · {cpu(beat.extra?.cpuPct)}
              </span>
            </div>
            <div>
              <div className="field__label">Host load (1 min)</div>
              <span className="mono">
                {beat.load1.toFixed(2)} on {beat.hostCpus} cores
              </span>
            </div>
            <div>
              <div className="field__label">Up</div>
              <span className="mono">{since(beat.startedAt)}</span>
            </div>
          </div>
        ) : (
          <p className="muted">{q.isPending ? "Loading…" : "No heartbeat from hub-bots."}</p>
        )}
      </Card>

      {selected && (
        <div ref={detailRef}>
          <BotDetail
            bot={selected}
            proc={proc.get(selected.id)}
            issues={issues.get(selected.id) ?? []}
            onClose={() => setOpen(null)}
            onLogs={() => onLogs(selected.id)}
          />
        </div>
      )}

      <Card
        title={`Bots · ${rows.length}`}
        actions={
          <>
            <input
              className="filter"
              type="search"
              aria-label="Search bots"
              placeholder="Name, owner or market"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="segmented" role="group" aria-label="Show">
              {(["all", "problems", "running", "stopped"] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  className={filter === f ? "is-active" : undefined}
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                >
                  {f[0]!.toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </>
        }
        flush
      >
        <SortableTable
          rows={rows}
          loading={q.isPending}
          rowKey={(b) => b.id}
          caption="Bots with their health and resources"
          initialSort={{ key: "health", direction: "asc" }}
          empty={<p className="card__body muted">No bots match.</p>}
          columns={[
            {
              key: "health",
              label: "Health",
              render: (b) => {
                const h = healthOf(b, issues.get(b.id));
                return (
                  <span
                    title={issues
                      .get(b.id)
                      ?.map((a) => a.title)
                      .join("\n")}
                  >
                    <HealthDot health={h} />
                  </span>
                );
              },
              sortValue: (b) => ({ error: 0, warn: 1, ok: 2, off: 3 })[healthOf(b, issues.get(b.id))],
              firstDirection: "asc",
            },
            {
              key: "name",
              label: "Bot",
              render: (b) => (
                <span className="stack-tight">
                  <Link to={`/b/${b.id}`}>{b.name}</Link>
                  <span className="muted small">
                    @{b.username}
                    {b.is_simulated ? " (sim)" : ""}
                  </span>
                </span>
              ),
              sortValue: (b) => b.name,
              firstDirection: "asc",
            },
            {
              key: "market",
              label: "Market",
              render: (b) => (
                <span className="mono small">
                  {b.symbol} {b.timeframe} <MarketTag marketType={b.market_type} leverage={b.leverage} />
                </span>
              ),
              sortValue: (b) => `${b.symbol}${b.timeframe}`,
              firstDirection: "asc",
            },
            directionColumn(),
            {
              key: "state",
              label: "State",
              render: (b) => <BotState status={b.status} desired={b.desired_state} />,
              sortValue: (b) => b.status,
              firstDirection: "asc",
            },
            {
              key: "up",
              label: "Up",
              align: "right",
              render: (b) => {
                const p = proc.get(b.id)?.process;
                return p && proc.get(b.id)?.running ? since(p.startedAt) : "—";
              },
              sortValue: (b) => {
                const p = proc.get(b.id);
                return p?.running && p.process ? -p.process.startedAt : null;
              },
            },
            {
              key: "cpu",
              label: "CPU",
              align: "right",
              render: (b) => cpu(proc.get(b.id)?.process?.resources?.cpuPct),
              sortValue: (b) => proc.get(b.id)?.process?.resources?.cpuPct ?? null,
            },
            {
              key: "mem",
              label: "Memory",
              align: "right",
              render: (b) => bytes(proc.get(b.id)?.process?.resources?.rssBytes ?? null),
              sortValue: (b) => proc.get(b.id)?.process?.resources?.rssBytes ?? null,
            },
            {
              key: "bar",
              label: "Last bar",
              align: "right",
              render: (b) => timeAgo(b.last_bar_at),
              sortValue: (b) => b.last_bar_at,
            },
            {
              key: "errors",
              label: "Errors 24h",
              align: "right",
              title: "Error-level log lines in the last 24 hours (warnings in brackets)",
              render: (b) => (
                <span className={b.errors24h ? "down" : "muted"}>
                  {b.errors24h}
                  {b.warnings24h ? <span className="muted"> ({b.warnings24h})</span> : null}
                </span>
              ),
              sortValue: (b) => b.errors24h * 1000 + b.warnings24h,
            },
            {
              key: "crashes",
              label: "Crashes",
              align: "right",
              render: (b) => proc.get(b.id)?.process?.crashes ?? 0,
              sortValue: (b) => proc.get(b.id)?.process?.crashes ?? 0,
            },
            {
              key: "ret",
              label: "Return",
              align: "right",
              render: (b) => <span className={tone(b.return_pct)}>{pct(b.return_pct, 2)}</span>,
              sortValue: (b) => b.return_pct,
            },
            {
              key: "open",
              label: <span className="sr-only">Details</span>,
              render: (b) => (
                <button
                  type="button"
                  className={`btn btn--sm${open === b.id ? " is-on" : ""}`}
                  aria-expanded={open === b.id}
                  aria-label={`Details for ${b.name}`}
                  onClick={() => setOpen(open === b.id ? null : b.id)}
                >
                  Details
                </button>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}

function BotDetail({
  bot,
  proc,
  issues,
  onClose,
  onLogs,
}: {
  bot: FleetBot;
  proc: Process | undefined;
  issues: OpsAlert[];
  onClose: () => void;
  onLogs: () => void;
}) {
  const qc = useQueryClient();
  const log = useQuery({
    queryKey: ["admin", "bot-events", { bot: bot.id, limit: 40 }],
    queryFn: () => api<{ items: LogLine[] }>(`/admin/bot-events?bot=${bot.id}&limit=40`),
    refetchInterval: 15_000,
  });
  const act = useMutation({
    mutationFn: (action: "start" | "stop" | "restart") =>
      api(`/admin/bots/${bot.id}/${action}`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "fleet"] });
      void qc.invalidateQueries({ queryKey: ["admin", "alerts"] });
      void qc.invalidateQueries({ queryKey: ["admin", "bot-events"] });
    },
  });
  const p = proc?.process;
  const runs = bot.desired_state === "running";
  return (
    <Card
      title={
        <span>
          {bot.name} <span className="muted small">@{bot.username}</span>
        </span>
      }
      actions={
        <>
          {runs && (
            <button
              type="button"
              className="btn btn--sm"
              disabled={act.isPending}
              onClick={() => act.mutate("restart")}
            >
              Restart
            </button>
          )}
          <button
            type="button"
            className={`btn btn--sm${runs ? " btn--danger" : " btn--primary"}`}
            disabled={act.isPending}
            onClick={() => {
              if (runs && !window.confirm(`Stop "${bot.name}"? Its owner will see it stopped.`)) return;
              act.mutate(runs ? "stop" : "start");
            }}
          >
            {runs ? "Stop" : "Start"}
          </button>
          <Link className="btn btn--sm" to={`/b/${bot.id}`}>
            Bot page
          </Link>
          <button type="button" className="btn btn--sm btn--ghost" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="stack">
        {act.error && <Alert tone="error">{act.error.message}</Alert>}
        {act.isSuccess && <Alert tone="ok">Done. The list updates in a moment.</Alert>}
        {issues.length > 0 && <AlertList items={issues} />}
        <div className="stat-grid stat-grid--pairs">
          <Stat label="Process" value={proc?.running ? `pid ${p?.pid ?? "?"}` : "not running"} />
          <Stat label="Up" value={proc?.running && p ? since(p.startedAt) : "—"} />
          <Stat label="CPU" value={cpu(p?.resources?.cpuPct)} hint="Share of one core" />
          <Stat label="Memory" value={bytes(p?.resources?.rssBytes ?? null)} hint="Resident set size" />
          <Stat label="Heap" value={bytes(p?.resources?.heapUsedBytes ?? null)} />
          <Stat label="Crashes" value={p?.crashes ?? 0} hint="Since hub-bots started" />
          <Stat label="Gaps (24h / all)" value={`${bot.gaps24h} / ${bot.gap_count}`} />
          <Stat label="Trades" value={bot.trade_count} />
        </div>
        <div className="row row--between">
          <span className="field__label">Recent log</span>
          <button type="button" className="link-btn small" onClick={onLogs}>
            Open in the log viewer
          </button>
        </div>
        <LogTail lines={log.data?.items ?? []} loading={log.isPending} />
      </div>
    </Card>
  );
}

function LogTail({ lines, loading }: { lines: LogLine[]; loading: boolean }) {
  // Newest at the bottom, like a terminal: open scrolled to it.
  const box = useRef<HTMLDivElement>(null);
  const newest = lines[0]?.id;
  useEffect(() => {
    if (box.current) box.current.scrollTop = box.current.scrollHeight;
  }, [newest]);
  if (loading) return <p className="muted">Loading the log…</p>;
  if (!lines.length) return <p className="muted">Nothing logged yet.</p>;
  return (
    <div className="log-tail" ref={box} tabIndex={0} role="log" aria-label="Recent log lines">
      {[...lines].reverse().map((l) => (
        <div key={l.id} className={`log-line log-line--${l.level}`}>
          <span className="muted">{new Date(l.created_at).toLocaleString()}</span> [{l.source}] {l.message}
        </div>
      ))}
    </div>
  );
}

function LogsView({ bot, onBot }: { bot: string; onBot: (id: string) => void }) {
  const fleet = useFleet();
  const [level, setLevel] = useState<"" | "warn" | "error">("");
  const [source, setSource] = useState("");
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [live, setLive] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setSearch(text), 350);
    return () => clearTimeout(t);
  }, [text]);
  const params = new URLSearchParams();
  if (bot) params.set("bot", bot);
  if (level) params.set("level", level);
  if (source) params.set("source", source);
  if (search.trim()) params.set("q", search.trim());
  params.set("limit", "100");
  const q = useInfiniteQuery({
    queryKey: ["admin", "bot-events", Object.fromEntries(params)],
    queryFn: ({ pageParam }) =>
      api<{ items: LogLine[]; nextBefore: number | null }>(
        `/admin/bot-events?${params.toString()}${pageParam ? `&before=${pageParam}` : ""}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    refetchInterval: live ? 10_000 : false,
  });
  const lines = q.data?.pages.flatMap((p) => p.items) ?? [];
  const bots = [...(fleet.data?.bots ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Card title="Bot logs" flush>
      <div className="card__body filter-row ops-log-filters">
        <label className="field">
          <span className="field__label">Bot</span>
          <select value={bot} onChange={(e) => onBot(e.target.value)}>
            <option value="">All bots</option>
            <option value="system">The runner itself (no bot)</option>
            {bots.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} (@{b.username})
              </option>
            ))}
          </select>
        </label>
        <div className="field">
          <span className="field__label" id="log-level">
            Level
          </span>
          <div className="segmented" role="group" aria-labelledby="log-level">
            {(
              [
                ["", "Everything"],
                ["warn", "Warnings and errors"],
                ["error", "Errors"],
              ] as const
            ).map(([v, label]) => (
              <button
                key={v}
                type="button"
                className={level === v ? "is-active" : undefined}
                aria-pressed={level === v}
                onClick={() => setLevel(v)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <label className="field">
          <span className="field__label">Source</span>
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Any</option>
            <option value="bot">The bot</option>
            <option value="hub">The runner</option>
            <option value="feed">Market feeds</option>
          </select>
        </label>
        <label className="field">
          <span className="field__label">Contains</span>
          <input
            type="search"
            value={text}
            placeholder="crashed, offline…"
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} /> Follow new
          lines
        </label>
      </div>
      {q.error && <Alert tone="error">{q.error.message}</Alert>}
      <div className="table-wrap">
        <table>
          <caption className="sr-only">Bot log lines, newest first</caption>
          <thead>
            <tr>
              <th>When</th>
              <th>Level</th>
              <th>Source</th>
              <th>Bot</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id}>
                <td className="mono small nowrap">{new Date(l.created_at).toLocaleString()}</td>
                <td>
                  <span
                    className={`badge${l.level === "error" ? " badge--danger" : l.level === "warn" ? " badge--warn" : ""}`}
                  >
                    {l.level}
                  </span>
                </td>
                <td className="small">{l.source}</td>
                <td className="small">
                  {l.bot_id ? (
                    <button type="button" className="link-btn" onClick={() => onBot(l.bot_id!)}>
                      {l.bot_name ?? l.bot_id.slice(0, 8)}
                    </button>
                  ) : (
                    <span className="muted">runner</span>
                  )}
                </td>
                <td className="mono small log-message">{l.message}</td>
              </tr>
            ))}
            {!q.isPending && !lines.length && (
              <tr>
                <td colSpan={5} className="muted">
                  No log lines match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {q.hasNextPage && (
        <div className="card__body">
          <button
            type="button"
            className="btn btn--sm"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            {q.isFetchingNextPage ? "Loading…" : "Older lines"}
          </button>
        </div>
      )}
    </Card>
  );
}

function FeedsView() {
  const q = useFleet();
  const feeds = q.data?.runner?.feeds ?? [];
  const live = feeds.filter((f) => f.state === "live").length;
  return (
    <div className="stack">
      <Card title="Market feeds">
        <p className="muted small">
          One websocket per market and timeframe, shared by every bot on it. A feed that is not live means its
          bots get no new bars.
        </p>
        <div className="stat-grid stat-grid--pairs">
          <Stat label="Feeds open" value={feeds.length} />
          <Stat label="Live" value={live} />
          <Stat label="Not live" value={feeds.length - live} />
          <Stat
            label="Reconnects"
            value={feeds.reduce((s, f) => s + f.reconnects, 0)}
            hint="Since hub-bots started"
          />
        </div>
      </Card>
      <Card flush>
        <SortableTable
          rows={feeds}
          loading={q.isPending}
          rowKey={(f) => f.stream}
          caption="Market feeds"
          initialSort={{ key: "state", direction: "asc" }}
          columns={[
            {
              key: "stream",
              label: "Stream",
              render: (f) => <span className="mono small">{f.stream.replace(/\|/g, " ")}</span>,
              sortValue: (f) => f.stream,
              firstDirection: "asc",
            },
            {
              key: "state",
              label: "State",
              render: (f) => (
                <HealthDot
                  health={f.state === "live" ? "ok" : f.state === "backfilling" ? "warn" : "error"}
                  label={f.state}
                />
              ),
              sortValue: (f) => (f.state === "live" ? 1 : 0),
              firstDirection: "asc",
            },
            {
              key: "since",
              label: "For",
              align: "right",
              render: (f) => since(f.stateSince),
              sortValue: (f) => -f.stateSince,
            },
            {
              key: "subs",
              label: "Bots",
              align: "right",
              render: (f) => f.subscribers,
              sortValue: (f) => f.subscribers,
            },
            {
              key: "last",
              label: "Last bar",
              align: "right",
              render: (f) => timeAgo(f.lastCandleAt),
              sortValue: (f) => f.lastCandleAt,
            },
            {
              key: "rc",
              label: "Reconnects",
              align: "right",
              render: (f) => f.reconnects,
              sortValue: (f) => f.reconnects,
            },
          ]}
        />
      </Card>
    </div>
  );
}
